import { useState } from "react";
import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import { Menu, X, Wallet, Layers, Star, User } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import NotificationBell from "../../components/NotificationBell";

const LINKS = [
  ["/app", "Portfolio", true],
  ["/app/deals", "Deals", false],
  ["/app/pipeline", "Pipeline", false],
  ["/app/interests", "Interests", false],
  ["/app/calculator", "Calculator", false],
  ["/app/documents", "Documents", false],
  ["/app/distributions", "Distributions", false],
  ["/app/transfers", "Transfers", false],
  ["/app/account", "Account", false],
];

const navCls = ({ isActive }) =>
  `t-label transition-colors duration-150 pb-1 border-b ${isActive ? "border-[#E8E6E1] !text-[#E8E6E1]" : "border-transparent hover:!text-[#E8E6E1]"}`;

const mobileCls = ({ isActive }) =>
  `t-label py-3 hairline ${isActive ? "!text-[#E8E6E1]" : "hover:!text-[#E8E6E1]"}`;

const BOTTOM = [
  ["/app", "Portfolio", true, Wallet],
  ["/app/deals", "Deals", false, Layers],
  ["/app/interests", "Interests", false, Star],
  ["/app/account", "Account", false, User],
];

export default function MemberLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-20" style={{ borderBottom: "1px solid var(--rule)", background: "#0E1012" }}>
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between gap-6">
          <Link to="/app" className="flex items-center gap-3" data-testid="member-home-link" onClick={() => setOpen(false)}>
            <span className="solera-symbol" />
            <span className="wordmark text-bone text-sm">Solera</span>
          </Link>
          <nav className="hidden lg:flex items-center gap-6">
            {LINKS.map(([to, label, end]) => (
              <NavLink key={to} to={to} end={end} className={navCls} data-testid={`nav-${label.toLowerCase()}`}>{label}</NavLink>
            ))}
            {user?.role === "admin" && <NavLink to="/admin" className={navCls} data-testid="nav-admin">Admin</NavLink>}
          </nav>
          <div className="flex items-center gap-5">
            <NotificationBell />
            <span className="t-cap text-muted-s hidden md:inline" data-testid="member-identity">
              {user?.preferences?.entity_name || user?.name}
            </span>
            <button onClick={doLogout} className="t-label hover:!text-[#E8E6E1] transition-colors duration-150 hidden lg:inline" data-testid="logout-button">
              Logout
            </button>
            <button onClick={() => setOpen((v) => !v)} className="lg:hidden text-bone -mr-1 p-1" data-testid="mobile-menu-button" aria-label={open ? "Close menu" : "Open menu"}>
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden" style={{ borderTop: "1px solid var(--rule)" }} data-testid="mobile-menu">
            <nav className="max-w-6xl mx-auto px-6 py-3 flex flex-col">
              {LINKS.map(([to, label, end]) => (
                <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)} className={mobileCls} data-testid={`mobile-nav-${label.toLowerCase()}`}>{label}</NavLink>
              ))}
              {user?.role === "admin" && (
                <NavLink to="/admin" onClick={() => setOpen(false)} className={mobileCls} data-testid="mobile-nav-admin">Admin</NavLink>
              )}
              <button onClick={doLogout} className="t-label py-3 hairline text-left hover:!text-[#E8E6E1]" data-testid="mobile-logout-button">Logout</button>
            </nav>
          </div>
        )}
      </header>
      <main className="flex-1 max-w-6xl w-full mx-auto px-6 pt-12 pb-28 lg:pb-12">
        <Outlet />
      </main>
      <footer className="hairline">
        <div className="max-w-6xl mx-auto px-6 pt-8 pb-24 lg:pb-8">
          <p className="t-cap text-muted-s measure">
            Confidential and for members only. Your links are issued to you and are not
            transferable. Nothing on this surface is investment advice.
          </p>
        </div>
      </footer>
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 flex" style={{ background: "#0E1012", borderTop: "1px solid var(--rule)" }} data-testid="bottom-tab-bar">
        {BOTTOM.map(([to, label, end, Icon]) => (
          <NavLink key={to} to={to} end={end}
            className={({ isActive }) => `flex-1 flex flex-col items-center gap-1 py-2.5 ${isActive ? "text-bone" : "text-muted-s"}`}
            data-testid={`tab-${label.toLowerCase()}`}>
            <Icon size={18} strokeWidth={1.5} />
            <span className="t-label" style={{ fontSize: "9px", letterSpacing: "0.1em" }}>{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
